import React, { useState } from 'react';
import { FaFilter, FaTimes, FaCheck, FaQuestionCircle } from 'react-icons/fa';
import ConditionRow from '../common/ConditionRow';
import DateFilterLiteralsHelpModal from '../common/DateFilterLiteralsHelpModal';
import { isOperatorValueRequired } from '../../utils/conditionBuilderUtils';
import {
  toConditionRowOperator,
} from '../../utils/reportFilterOperators';
import {
  REPORT_RUNTIME_FILTER_CONDITION_ROW_OPTIONS,
  buildReportFilterConditionState,
} from '../../utils/reportFilterConditionUtils';
import './css/ReportDataViewer.css';

/**
 * Shared run-time report filter modal (report viewer, dashboard widgets, etc.).
 */
const ReportRuntimeFilterModal = ({
  show,
  onClose,
  title = 'Manage Report Filters',
  filterLogic,
  filterCustomLogic,
  filterDraft = [],
  filterAvailableFields = [],
  onFilterDraftChange,
  onApply,
  onReset,
  applyLabel = 'Apply Filters',
}) => {
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);

  if (!show) return null;

  return (
    <>
      <div className="report-data-viewer report-runtime-filter-modal-root">
        <div
          className="filter-modal-overlay"
          onClick={onClose}
          role="presentation"
        >
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="filter-modal-header">
              <h3>
                <FaFilter /> {title}
              </h3>
              <button type="button" onClick={onClose} className="close-modal-btn">
                <FaTimes />
              </button>
            </div>

            {(filterLogic || filterDraft.length > 1) && (
              <div className="filter-logic-display">
                <strong>Filter Logic:</strong> {filterLogic || 'AND'}
                {filterCustomLogic && (
                  <div className="custom-filter-logic">
                    <strong>Custom Logic:</strong> {filterCustomLogic}
                  </div>
                )}
              </div>
            )}

            <div className="filter-modal-content">
              <p className="text-muted small mb-2 px-1">
                Changes apply only to this session and are not saved to the widget.
              </p>
              <div className="filters-table-container">
                <table className="filters-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field, operator &amp; value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterDraft.length > 0 ? (
                      filterDraft.map((filter, index) => {
                        const catalogField =
                          filterAvailableFields.find(
                            (f) => String(f.id) === String(filter.field_config_id)
                          ) || null;
                        const selectedField = catalogField || {
                          id: filter.field_config_id,
                          field_name: filter.field_name,
                          field_label: filter.field_label,
                          data_type: filter.data_type,
                          object_name: filter.object_name,
                          picklist_values: filter.picklist_values,
                        };
                        const dt = (selectedField?.data_type || '').toLowerCase();
                        const showDateHint =
                          (dt === 'date' || dt === 'datetime') &&
                          isOperatorValueRequired(
                            toConditionRowOperator(filter.operator)
                          );
                        const readOnly = filter.is_editable === false;

                        return (
                          <tr key={filter.id || index} className="filter-row">
                            <td className="filter-number">{index + 1}</td>
                            <td className="filter-condition-cell">
                              <ConditionRow
                                condition={buildReportFilterConditionState(
                                  filter,
                                  filterAvailableFields.length > 0
                                    ? filterAvailableFields
                                    : [selectedField]
                                )}
                                fields={
                                  filterAvailableFields.length > 0
                                    ? filterAvailableFields
                                    : [selectedField]
                                }
                                onChange={onFilterDraftChange(index)}
                                index={index}
                                readOnly={readOnly || filter.is_editable === false}
                                options={{
                                  ...REPORT_RUNTIME_FILTER_CONDITION_ROW_OPTIONS,
                                  valueFields:
                                    filterAvailableFields.length > 0
                                      ? filterAvailableFields
                                      : [selectedField],
                                }}
                              />
                              {showDateHint && (
                                <div className="small text-muted mt-1 d-flex align-items-center gap-1">
                                  <FaQuestionCircle
                                    className="text-primary"
                                    size={12}
                                    style={{ cursor: 'pointer' }}
                                    title="Date filter help"
                                    onClick={() => setShowDateHelpModal(true)}
                                  />
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="text-primary"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setShowDateHelpModal(true)}
                                    onKeyDown={(e) =>
                                      e.key === 'Enter' && setShowDateHelpModal(true)
                                    }
                                  >
                                    Date / datetime literals and formats
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={2}
                          style={{ textAlign: 'center', padding: '20px', color: '#666' }}
                        >
                          No filters available for this report.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="filter-modal-footer">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={onReset} className="btn btn-outline-secondary">
                Reset Filters
              </button>
              <button type="button" onClick={onApply} className="btn btn-primary">
                <FaCheck /> {applyLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      <DateFilterLiteralsHelpModal
        show={showDateHelpModal}
        onHide={() => setShowDateHelpModal(false)}
      />
    </>
  );
};

export default ReportRuntimeFilterModal;
